import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, CardContent, Typography, Box, Tabs, Tab, Button, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, Chip, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControl,
  InputLabel, Select, MenuItem, Alert, Snackbar, Grid
} from '@mui/material';
import PolicyIcon from '@mui/icons-material/Policy';
import AssessmentIcon from '@mui/icons-material/Assessment';
import DownloadIcon from '@mui/icons-material/Download';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useAuth } from '../../contexts/AuthContext';
import { apiFetch, getAuthenticatedApiUrl } from '../../api/client';
import { withUnidade } from '../../utils/storageByUnit';

const TIPOS_RELATORIO = [
  { value: 'AUDITORIA_COMPLETA', label: 'Auditoria completa' },
  { value: 'ACESSOS_DADOS', label: 'Acessos a dados' },
  { value: 'ALTERACOES_DADOS', label: 'Alterações de dados' },
  { value: 'CONSENTIMENTOS', label: 'Consentimentos' },
  { value: 'AUTENTICACAO', label: 'Autenticação' }
];

const TIPO_LABELS = {
  PRIVACIDADE: 'Privacidade',
  RETENCAO: 'Retenção',
  SEGURANCA: 'Segurança',
  TERMOS_USO: 'Termos de uso'
};

function TabPanel({ children, value, index }) {
  if (value !== index) return null;
  return <Box sx={{ pt: 2 }}>{children}</Box>;
}

export default function LgpdPage() {
  const { isAdmin, isCargoCorporativo, unidadeSelecionada } = useAuth();
  const isLgpdAdmin = isAdmin || isCargoCorporativo;
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [politicas, setPoliticas] = useState([]);
  const [auditoria, setAuditoria] = useState([]);
  const [relatorios, setRelatorios] = useState([]);
  const [resumo, setResumo] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [viewPolitica, setViewPolitica] = useState(null);
  const [novaPoliticaOpen, setNovaPoliticaOpen] = useState(false);
  const [novaPolitica, setNovaPolitica] = useState({ tipo: 'PRIVACIDADE', titulo: '', versao: '', conteudo: '' });
  const [filtrosAuditoria, setFiltrosAuditoria] = useState({ dataInicio: '', dataFim: '', categoria: '' });
  const [gerarRelatorio, setGerarRelatorio] = useState({ tipo: 'AUDITORIA_COMPLETA', formato: 'pdf', dataInicio: '', dataFim: '' });
  const [gerando, setGerando] = useState(false);

  const apiPath = useCallback((path) => withUnidade(path, unidadeSelecionada), [unidadeSelecionada?.codigo]);

  const fetchPoliticas = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch(apiPath('/api/lgpd/politicas'));
      if (res.ok) setPoliticas(await res.json());
    } finally {
      setLoading(false);
    }
  }, [apiPath]);

  const fetchAuditoria = useCallback(async () => {
    if (!isLgpdAdmin) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filtrosAuditoria.dataInicio) params.append('dataInicio', filtrosAuditoria.dataInicio);
      if (filtrosAuditoria.dataFim) params.append('dataFim', filtrosAuditoria.dataFim);
      if (filtrosAuditoria.categoria) params.append('categoria', filtrosAuditoria.categoria);
      const qs = params.toString();
      const res = await apiFetch(apiPath(`/api/lgpd/auditoria${qs ? `?${qs}` : ''}`));
      if (res.ok) setAuditoria(await res.json());
    } finally {
      setLoading(false);
    }
  }, [apiPath, filtrosAuditoria, isLgpdAdmin]);

  const fetchRelatorios = useCallback(async () => {
    if (!isLgpdAdmin) return;
    const [resRel, resSum] = await Promise.all([
      apiFetch(apiPath('/api/lgpd/relatorios')),
      apiFetch(apiPath('/api/lgpd/auditoria/resumo'))
    ]);
    if (resRel.ok) setRelatorios(await resRel.json());
    if (resSum.ok) setResumo(await resSum.json());
  }, [apiPath, isLgpdAdmin]);

  useEffect(() => {
    fetchPoliticas();
  }, [fetchPoliticas]);

  useEffect(() => {
    if (tab === 1 && isLgpdAdmin) fetchAuditoria();
    if (tab === 2 && isLgpdAdmin) fetchRelatorios();
  }, [tab, isLgpdAdmin, fetchAuditoria, fetchRelatorios]);

  const handlePublicarPolitica = async () => {
    try {
      const res = await apiFetch(apiPath('/api/lgpd/politicas'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(novaPolitica)
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Erro ao publicar');
      }
      setSnackbar({ open: true, message: 'Nova versão publicada. Usuários precisarão aceitar novamente.', severity: 'success' });
      setNovaPoliticaOpen(false);
      fetchPoliticas();
    } catch (e) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
    }
  };

  const handleGerarRelatorio = async () => {
    setGerando(true);
    try {
      const res = await apiFetch(apiPath('/api/lgpd/relatorios/gerar'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(gerarRelatorio)
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Erro ao gerar relatório');
      }
      const data = await res.json();
      setSnackbar({ open: true, message: `Relatório gerado (${data.total_registros} registros)`, severity: 'success' });
      fetchRelatorios();
      if (data.pdf_url) window.open(getAuthenticatedApiUrl(data.pdf_url), '_blank');
    } catch (e) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
    } finally {
      setGerando(false);
    }
  };

  const formatDate = (s) => {
    if (!s) return '-';
    const d = new Date(s);
    return isNaN(d.getTime()) ? s : d.toLocaleString('pt-BR');
  };

  return (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <PolicyIcon color="primary" />
          <Typography variant="h6" fontWeight={700}>LGPD — Privacidade e Auditoria</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Políticas de proteção de dados, trilha de auditoria e relatórios para conformidade.
        </Typography>

        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label="Políticas" />
          {isLgpdAdmin && <Tab label="Auditoria" icon={<AssessmentIcon fontSize="small" />} iconPosition="start" />}
          {isLgpdAdmin && <Tab label="Relatórios" />}
        </Tabs>

        <TabPanel value={tab} index={0}>
          {isLgpdAdmin && (
            <Box mb={2}>
              <Button startIcon={<AddIcon />} variant="contained" onClick={() => setNovaPoliticaOpen(true)}>
                Nova versão de política
              </Button>
            </Box>
          )}
          {loading ? <CircularProgress size={28} /> : (
            <Grid container spacing={2}>
              {politicas.map((p) => (
                <Grid item xs={12} md={6} key={p.id}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography fontWeight={600}>{p.titulo}</Typography>
                    <Box display="flex" gap={1} my={1}>
                      <Chip size="small" label={TIPO_LABELS[p.tipo] || p.tipo} />
                      <Chip size="small" variant="outlined" label={`v${p.versao}`} />
                    </Box>
                    <Button size="small" startIcon={<VisibilityIcon />} onClick={() => setViewPolitica(p)}>
                      Ler política
                    </Button>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}
        </TabPanel>

        {isLgpdAdmin && (
          <TabPanel value={tab} index={1}>
            <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
              <TextField type="date" size="small" label="De" InputLabelProps={{ shrink: true }}
                value={filtrosAuditoria.dataInicio} onChange={(e) => setFiltrosAuditoria({ ...filtrosAuditoria, dataInicio: e.target.value })} />
              <TextField type="date" size="small" label="Até" InputLabelProps={{ shrink: true }}
                value={filtrosAuditoria.dataFim} onChange={(e) => setFiltrosAuditoria({ ...filtrosAuditoria, dataFim: e.target.value })} />
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Categoria</InputLabel>
                <Select value={filtrosAuditoria.categoria} label="Categoria"
                  onChange={(e) => setFiltrosAuditoria({ ...filtrosAuditoria, categoria: e.target.value })}>
                  <MenuItem value="">Todas</MenuItem>
                  {['AUTENTICACAO', 'ACESSO', 'ALTERACAO', 'EXCLUSAO', 'EXPORTACAO', 'CONSENTIMENTO', 'POLITICA', 'RELATORIO'].map((c) => (
                    <MenuItem key={c} value={c}>{c}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button variant="outlined" onClick={fetchAuditoria}>Filtrar</Button>
            </Box>
            <TableContainer component={Paper} sx={{ maxHeight: 480 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Data</TableCell>
                    <TableCell>Usuário</TableCell>
                    <TableCell>Ação</TableCell>
                    <TableCell>Categoria</TableCell>
                    <TableCell>Entidade</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {auditoria.length === 0 ? (
                    <TableRow><TableCell colSpan={5} align="center">Nenhum registro</TableCell></TableRow>
                  ) : auditoria.map((a) => (
                    <TableRow key={a.id}>
                      <TableCell>{formatDate(a.created_at)}</TableCell>
                      <TableCell>{a.user_email || '-'}</TableCell>
                      <TableCell>{a.acao}</TableCell>
                      <TableCell><Chip size="small" label={a.categoria} /></TableCell>
                      <TableCell>{a.entidade ? `${a.entidade}${a.entidade_id ? ` #${a.entidade_id}` : ''}` : '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>
        )}

        {isLgpdAdmin && (
          <TabPanel value={tab} index={2}>
            {resumo && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Eventos nos últimos 7 dias: <strong>{resumo.eventosUltimos7Dias}</strong> ·
                Consentimentos registrados: <strong>{resumo.totalConsentimentos}</strong>
              </Alert>
            )}
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Typography fontWeight={600} mb={2}>Gerar relatório para auditoria</Typography>
              <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <InputLabel>Tipo</InputLabel>
                  <Select value={gerarRelatorio.tipo} label="Tipo"
                    onChange={(e) => setGerarRelatorio({ ...gerarRelatorio, tipo: e.target.value })}>
                    {TIPOS_RELATORIO.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 100 }}>
                  <InputLabel>Formato</InputLabel>
                  <Select value={gerarRelatorio.formato} label="Formato"
                    onChange={(e) => setGerarRelatorio({ ...gerarRelatorio, formato: e.target.value })}>
                    <MenuItem value="pdf">PDF</MenuItem>
                    <MenuItem value="json">JSON</MenuItem>
                  </Select>
                </FormControl>
                <TextField type="date" size="small" label="De" InputLabelProps={{ shrink: true }}
                  value={gerarRelatorio.dataInicio} onChange={(e) => setGerarRelatorio({ ...gerarRelatorio, dataInicio: e.target.value })} />
                <TextField type="date" size="small" label="Até" InputLabelProps={{ shrink: true }}
                  value={gerarRelatorio.dataFim} onChange={(e) => setGerarRelatorio({ ...gerarRelatorio, dataFim: e.target.value })} />
                <Button variant="contained" onClick={handleGerarRelatorio} disabled={gerando}>
                  {gerando ? 'Gerando...' : 'Gerar relatório'}
                </Button>
              </Box>
            </Paper>
            <Typography variant="subtitle1" fontWeight={600} mb={1}>Relatórios emitidos</Typography>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Data</TableCell>
                    <TableCell>Título</TableCell>
                    <TableCell>Registros</TableCell>
                    <TableCell>Emitido por</TableCell>
                    <TableCell align="right">Download</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {relatorios.length === 0 ? (
                    <TableRow><TableCell colSpan={5} align="center">Nenhum relatório gerado</TableCell></TableRow>
                  ) : relatorios.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell>{formatDate(r.created_at)}</TableCell>
                      <TableCell>{r.titulo}</TableCell>
                      <TableCell>{r.total_registros}</TableCell>
                      <TableCell>{r.gerado_por_email || '-'}</TableCell>
                      <TableCell align="right">
                        {r.download_url && (
                          <Button size="small" startIcon={<DownloadIcon />}
                            href={getAuthenticatedApiUrl(r.download_url)} target="_blank" rel="noopener noreferrer">
                            {r.formato?.toUpperCase()}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>
        )}
      </CardContent>

      <Dialog open={!!viewPolitica} onClose={() => setViewPolitica(null)} maxWidth="md" fullWidth>
        <DialogTitle>{viewPolitica?.titulo} (v{viewPolitica?.versao})</DialogTitle>
        <DialogContent dividers>
          <Typography component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: 14 }}>
            {viewPolitica?.conteudo}
          </Typography>
        </DialogContent>
        <DialogActions><Button onClick={() => setViewPolitica(null)}>Fechar</Button></DialogActions>
      </Dialog>

      <Dialog open={novaPoliticaOpen} onClose={() => setNovaPoliticaOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Publicar nova versão</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <FormControl fullWidth>
              <InputLabel>Tipo</InputLabel>
              <Select value={novaPolitica.tipo} label="Tipo"
                onChange={(e) => setNovaPolitica({ ...novaPolitica, tipo: e.target.value })}>
                {Object.entries(TIPO_LABELS).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField label="Título" fullWidth value={novaPolitica.titulo}
              onChange={(e) => setNovaPolitica({ ...novaPolitica, titulo: e.target.value })} />
            <TextField label="Versão (ex: 1.1)" fullWidth value={novaPolitica.versao}
              onChange={(e) => setNovaPolitica({ ...novaPolitica, versao: e.target.value })} />
            <TextField label="Conteúdo (Markdown)" fullWidth multiline rows={12} value={novaPolitica.conteudo}
              onChange={(e) => setNovaPolitica({ ...novaPolitica, conteudo: e.target.value })} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNovaPoliticaOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handlePublicarPolitica}
            disabled={!novaPolitica.titulo || !novaPolitica.versao || !novaPolitica.conteudo}>
            Publicar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Card>
  );
}
